import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormatters } from '@/shared/hooks/useFormatters/useFormatters.ts';
import { useZodForm } from '@/shared/hooks/useZodForm.ts';
import {
    getPositionSettingsFormSchema,
    PositionSettingsFormRules,
} from '@/shared/forms/PositionSettingsForm/schemas/getPositionSettingsFormSchema.ts';
import { FormProvider } from 'react-hook-form';
import { WizardFooter } from '@/shared/components/Wizard.tsx';
import { TextBoxFieldInput } from '@/shared/inputs/TextBoxFieldInput.tsx';
import { DatePickerFieldInput } from '@/shared/inputs/DatePickerFieldInput.tsx';
import { SwitchFieldInput } from '@/shared/inputs/SwitchFieldInput.tsx';
import { Button } from '@schuettflix/planum-react';
import { PositionSettingsValues } from '@/modules/position/components/PositionSettings/PositionSettings.tsx';

type PositionSettingsFormProps = {
    projectTitle?: string;
    formRules?: PositionSettingsFormRules;
    onSubmit: (positionSettings: PositionSettingsValues) => void;
    defaultValues?: Partial<PositionSettingsValues>;
    mode: 'create' | 'edit';
};

export const PositionSettingsForm: React.FC<PositionSettingsFormProps> = ({
    projectTitle,
    formRules,
    onSubmit,
    defaultValues,
    mode,
}) => {
    const { t } = useTranslation();

    const methods = useZodForm({
        schema: getPositionSettingsFormSchema({
            formRules,
            errorMessages: {
                required: t('product.addPosition.errorMessage.pleaseEnterValue'),
                dateRange: {
                    validFrom: t('position.settings.errorMessage.pleaseEnterValidStartDate'),
                    validTo: t('position.settings.errorMessage.pleaseEnterValidEndDate'),
                },
            },
        }),
        defaultValues: {
            active: defaultValues?.active ?? false,
            note: defaultValues?.note,
            dateRange: {
                validFrom: defaultValues?.validFrom,
                validTo: defaultValues?.validTo,
            },
        },
    });

    const { handleSubmit, setValue, watch, register } = methods;
    const formValues = watch();
    const isPositionInactive = formValues.dateRange?.validFrom > new Date();

    const handleSetToProjectRange = useCallback(() => {
        if (!formRules?.dateRange) return;

        setValue(
            'dateRange',
            {
                validFrom: new Date(formRules.dateRange.validFrom),
                validTo: new Date(formRules.dateRange.validTo),
            },
            { shouldValidate: true }
        );
    }, [formRules?.dateRange, setValue]);

    const handleFormSubmit = useCallback<React.FormEventHandler<HTMLFormElement>>(
        async event => {
            event.preventDefault();
            await handleSubmit(fieldValues => {
                onSubmit({
                    active: fieldValues.active,
                    validFrom: fieldValues.dateRange.validFrom,
                    validTo: fieldValues.dateRange.validTo,
                    note: fieldValues.note,
                });
            }, console.error)();
        },
        [handleSubmit, onSubmit]
    );

    return (
        <FormProvider {...methods}>
            <form
                onSubmit={handleFormSubmit}
                className="flex w-full flex-col items-stretch justify-between pt-4"
                data-test="position-settings-form"
            >
                <div className="flex flex-col gap-6">
                    <ProjectValidityHint
                        projectTitle={projectTitle}
                        dateRange={formRules?.dateRange}
                        onSetToProjectRange={handleSetToProjectRange}
                    />

                    <div className="flex flex-col gap-12">
                        <div className="flex justify-between">
                            <DatePickerFieldInput
                                {...register('dateRange')}
                                mode="range"
                                size="lg"
                                placeHolder={t('position.sections.positionSettings.datePicker.label')}
                                dataTest="position-validity-date-picker"
                                minDate={formRules?.dateRange?.validFrom}
                                maxDate={formRules?.dateRange?.validTo}
                                className="flex w-1/2"
                                onDatePickerFieldChange={dateRange => {
                                    if (!dateRange) return;

                                    if (new Date(dateRange.validFrom) > new Date()) {
                                        setValue('active', false);
                                    }
                                }}
                            />

                            <SwitchFieldInput
                                {...register('active')}
                                label={t('position.settings.state.label')}
                                size="md"
                                disabled={isPositionInactive}
                                dataTest="toggle-switch"
                            />
                        </div>

                        <section className="flex flex-col gap-1">
                            <label htmlFor="note" className="font-copy-sm-strong">
                                {t('position.sections.positionSettings.note.title')}
                            </label>
                            <TextBoxFieldInput
                                {...register('note')}
                                placeHolder={t('position.sections.positionSettings.note.placeHolder')}
                                rows={5}
                            />
                        </section>
                    </div>
                </div>
                <WizardFooter>
                    <Button
                        type="submit"
                        data-test="proceed-button"
                        variant={mode === 'edit' ? 'secondary' : 'primary'}
                    >
                        {t('position.settings.summaryButton')}
                    </Button>
                </WizardFooter>
            </form>
        </FormProvider>
    );
};

function ProjectValidityHint({
    projectTitle,
    dateRange,
    onSetToProjectRange,
}: {
    projectTitle?: string;
    dateRange?: { validFrom: string; validTo: string };
    onSetToProjectRange: () => void;
}) {
    const { t } = useTranslation();
    const { formatDate } = useFormatters();

    return (
        <p className="font-copy-md flex items-start gap-4">
            <strong className="font-copy-md-strong">{t('position.sections.positionSettings.datePicker.hint')}:</strong>
            <span>
                {t(`position.sections.positionSettings.projectValidityRange`, {
                    startDate: dateRange ? formatDate(new Date(dateRange.validFrom)) : undefined,
                    endDate: dateRange ? formatDate(new Date(dateRange.validTo)) : undefined,
                    projectTitle,
                })}
            </span>
            <button
                type="button"
                data-test="project-range-button"
                className="shrink-0 whitespace-nowrap underline outline-none"
                onClick={onSetToProjectRange}
            >
                {t('position.sections.positionSettings.datePicker.setToProjectRange')}
            </button>
        </p>
    );
}
